import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { filter, map, take } from 'rxjs/operators';

import { AuthService } from '@app/core/services/auth.service';
import { ClientRole } from '@app/core/models';

/**
 * Keeps corporate clients and sub-users inside the `/corporate` route tree.
 *
 * The client layout picks its shell (corporate vs personal sidebar) from the
 * URL prefix — `url.startsWith('/corporate')`. So whenever a corporate user
 * lands on a personal-tree URL (a stray `/dashboard`, a `/kyc` redirect, a
 * deep link or bookmark), they'd render the *personal* shell with the wrong
 * menus. This guard, mounted ONLY on the personal ('') layout, redirects any
 * authenticated corporate user to the `/corporate` mirror of the same route,
 * preserving the path and query string.
 *
 * It is the structural backstop behind the individual role-aware redirects
 * (onboarding success, KycRequiredGuard): even a future hardcoded personal
 * redirect can't strand a corporate user in the personal shell.
 *
 * Notes:
 * - Personal clients and unauthenticated requests pass through untouched
 *   (authGuard, mounted upstream, owns the auth decision).
 * - Every child of the personal layout is a SHARED_FEATURE_ROUTE, so a
 *   `/corporate` mirror always exists — the redirect never 404s.
 * - No redirect loop: the guard is absent from the `/corporate` tree.
 * - Page-refresh race: when `restoreInProgress()` is true we await it before
 *   reading the role, matching authGuard / kycRequiredGuard.
 */
export const corporateTreeGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const evaluate = (): boolean | UrlTree => {
    const role = authService.getUserRole();

    // Not authenticated / role unknown — defer to authGuard upstream.
    if (!role) return true;

    const isCorporate =
      role === ClientRole.CORPORATE_OWNER || role === ClientRole.CORPORATE_USER;
    if (!isCorporate) return true;

    // Mounted on the personal ('') layout only, so state.url is always a
    // personal-tree URL here — prefix it to reach the corporate mirror.
    return router.parseUrl('/corporate' + state.url);
  };

  if (authService.restoreInProgress()) {
    return toObservable(authService.restoreInProgress).pipe(
      filter((inProgress) => !inProgress),
      take(1),
      map(() => evaluate()),
    );
  }

  return evaluate();
};
